import { useMemo, useState } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import BudgetModal from "components/modals/BudgetModal";
import { Badge, Btn, Card, useIsMobile } from "components/ui/index";
import { MONTHS_FULL } from "constants/index";
import { T } from "theme/index";
import { getBudgetRealization, getBudgetSummary, getBudgetMoM } from "engine/budgets";
import { getWalletMonth, getTransferNeeded } from "engine/wallets";
import { getMonthlySummary } from "engine/transactions";
import { fmtIDR, fmtShort } from "utils/format";

// ── Anggaran ──────────────────────────────────────────────────────────────────
export default function Anggaran({ transactions, categories, wallets, budgets, month, year, onSaveBudget }) {
  const isMobile = useIsMobile();
  const [showModal,  setShowModal]  = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  // ── Engine calls ──
  const monthBudgets = budgets.filter(b => b.month===month && b.year===year);
  const catData      = getBudgetRealization(budgets, transactions, categories, wallets, month, year);
  const { expData, invData, expBudget, expActual, invBudget, invActual, totalBudget, pieData } = getBudgetSummary(catData);
  const { prevExpActual: prevExp, prevInvActual: prevInv } = getBudgetMoM(transactions, month, year);


  // Ringkasan anggaran per rekening (untuk kemudahan transfer) — gabung expense+investasi
  const byWallet = useMemo(() => {
    const map = {};
    catData.forEach(cat => {
      if (!cat.walletId) return;
      if (!map[cat.walletId]) map[cat.walletId] = { wallet: cat.wallet, totalBudget:0, totalActual:0, cats:[] };
      map[cat.walletId].totalBudget += cat.budget;
      map[cat.walletId].totalActual += cat.actual;
      map[cat.walletId].cats.push(cat);
    });
    return Object.values(map).filter(g => g.wallet);
  }, [catData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Render baris kategori (dipakai 2 container)
  const CatRow = ({cat, i, len}) => {
    const pct  = cat.budget>0 ? Math.min((cat.actual/cat.budget)*100,100) : 0;
    const over = cat.budget>0 && cat.actual>cat.budget;
    return (
      <div style={{ marginBottom:i<len-1?14:0 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:5 }}>
          <div style={{ display:"flex", alignItems:"center", gap:7, flexWrap:"wrap" }}>
            <span style={{ fontSize:15 }}>{cat.icon}</span>
            <span style={{ color:T.text, fontSize:13, fontWeight:500 }}>{cat.name}</span>
            {cat.wallet && <span style={{ background:cat.wallet.color+"22", color:cat.wallet.color, border:`1px solid ${cat.wallet.color}33`, borderRadius:5, padding:"1px 7px", fontSize:10, fontWeight:700 }}>{cat.wallet.icon} {cat.wallet.name}</span>}
            {over && <Badge label="OVER" color={T.red} />}
          </div>
          <div style={{ fontSize:11, fontFamily:"monospace", fontVariantNumeric:"tabular-nums", color:T.muted }}>
            <span style={{ color:over?T.red:T.text }}>{isMobile ? fmtShort(cat.actual) : fmtIDR(cat.actual)}</span>
            {cat.budget>0 && <span> / {isMobile ? fmtShort(cat.budget) : fmtIDR(cat.budget)}</span>}
          </div>
        </div>
        {cat.budget>0 && (
          <div style={{ height:5, background:T.surface, borderRadius:3 }}>
            <div style={{ height:"100%", borderRadius:3, width:`${pct}%`, background:over?T.red:pct>80?T.orange:T.green }} />
          </div>
        )}
      </div>
    );
  };

  const monthIncome = getMonthlySummary(transactions, month, year).income;

  return (
    <div>
      {showModal && <BudgetModal existing={monthBudgets} allBudgets={budgets} categories={categories} wallets={wallets} month={month} year={year} actualIncome={monthIncome} onSave={onSaveBudget} onClose={() => setShowModal(false)} />}

      {/* Header + action bar */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
        <div style={{ color:T.text, fontWeight:700, fontSize:14 }}>{MONTHS_FULL[month]} {year}</div>
        <Btn onClick={() => setShowModal(true)} style={{ fontSize:12, padding:"8px 12px" }}>✏️ Atur Anggaran</Btn>
      </div>

      {/* Summary: pie alokasi + angka — 1 card kompak */}
      <Card style={{ padding:16, marginBottom:14 }}>
        <div style={{ display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
          {pieData.length > 0 ? (
            <ResponsiveContainer width={110} height={110}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={32} outerRadius={52} dataKey="value" paddingAngle={3}>
                  {pieData.map((e,i)=><Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip formatter={v=>fmtIDR(v)} contentStyle={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, fontSize:11, color:T.text }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ width:110, height:110, display:"flex", alignItems:"center", justifyContent:"center", color:T.muted, fontSize:11, textAlign:"center" }}>Belum ada<br/>anggaran</div>
          )}
          <div style={{ flex:1, minWidth:180 }}>
            {[
              ["💸 Pengeluaran", expBudget, expActual, T.red,  prevExp,  false],
              ["📈 Investasi",   invBudget, invActual, T.gold, prevInv,  true],
            ].map(([label, budget, actual, col, prev, upGood]) => {
              const pctOfTotal = totalBudget>0 ? (budget/totalBudget)*100 : 0;
              const mom = prev > 0 ? ((actual - prev) / prev) * 100 : null;
              const momUp = mom !== null && mom > 0;
              const momCol = mom === null ? T.muted : momUp ? (upGood ? T.green : T.red) : (upGood ? T.red : T.green);
              return (
                <div key={label} style={{ marginBottom:10 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:3, flexWrap:"wrap", gap:4 }}>
                    <span style={{ color:T.text, fontSize:12, fontWeight:600 }}>{label} <span style={{ color:col, fontWeight:800 }}>{pctOfTotal.toFixed(0)}%</span></span>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      {mom !== null && (
                        <span style={{ fontSize:10, fontWeight:700, color:momCol }}>
                          {momUp?"▲":"▼"} {Math.abs(mom).toFixed(1)}% vs bln lalu
                        </span>
                      )}
                      <span style={{ fontSize:11, fontFamily:"monospace", fontVariantNumeric:"tabular-nums", color:T.muted }}>{isMobile ? fmtShort(actual) : fmtIDR(actual)} / <span style={{ color:col }}>{isMobile ? fmtShort(budget) : fmtIDR(budget)}</span></span>
                    </div>
                  </div>
                  <div style={{ height:5, background:T.surface, borderRadius:3 }}>
                    <div style={{ height:"100%", borderRadius:3, width:`${pctOfTotal}%`, background:col }} />
                  </div>
                </div>
              );
            })}
            <div style={{ color:T.muted, fontSize:11, marginTop:4 }}>Total anggaran: <span style={{ color:T.text, fontFamily:"monospace", fontVariantNumeric:"tabular-nums", fontWeight:700 }}>{fmtIDR(totalBudget)}</span></div>
          </div>
        </div>
      </Card>

      {/* Summary per rekening — bantuan transfer */}
      {byWallet.length > 0 && (
        <Card style={{ padding:16, marginBottom:14 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
            <div style={{ color:T.text, fontWeight:700, fontSize:13 }}>💸 Transfer ke Rekening</div>
            <span style={{ color:T.muted, fontSize:11 }}>anggaran − saldo saat ini</span>
          </div>

          {/* Logic: setelah tgl 10 bulan berjalan atau bulan lampau, dana dianggap sudah dialokasikan */}
          {(() => {
            const now        = new Date();
            const isThisMonth = month === now.getMonth() && year === now.getFullYear();
            const isPastMonth = year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth());
            const isFuture    = year > now.getFullYear() || (year === now.getFullYear() && month > now.getMonth());
            const pastCutoff  = isThisMonth && now.getDate() > 10;
            const showDone    = isPastMonth || pastCutoff;

            if (showDone && !showDetail) {
              const reason = isPastMonth
                ? `Bulan ${MONTHS_FULL[month]} sudah selesai`
                : `Sudah melewati tanggal 10 ${MONTHS_FULL[month]}`;
              return (
                <div style={{ background:T.green+"10", border:`1px solid ${T.green}30`, borderRadius:10, padding:"16px 18px", textAlign:"center" }}>
                  <div style={{ fontSize:22, marginBottom:8 }}>✅</div>
                  <div style={{ color:T.green, fontWeight:700, fontSize:14, marginBottom:4 }}>Dana sudah dialokasikan</div>
                  <div style={{ color:T.muted, fontSize:12 }}>{reason} — semua dana bulan ini sudah masuk dan dialokasikan ke masing-masing rekening.</div>
                  <div style={{ marginTop:12, display:"flex", gap:8, flexWrap:"wrap", justifyContent:"center" }}>
                    {byWallet.map(g => (
                      <span key={g.wallet.id} style={{ background:T.surface, borderRadius:6, padding:"4px 10px", fontSize:11, color:T.muted, display:"flex", alignItems:"center", gap:5 }}>
                        {g.wallet.icon} {g.wallet.name}: <span style={{ color:T.text, fontFamily:"monospace", fontVariantNumeric:"tabular-nums" }}>{isMobile ? fmtShort(g.totalBudget) : fmtIDR(g.totalBudget)}</span>
                      </span>
                    ))}
                  </div>
                  <button onClick={() => setShowDetail(true)}
                    style={{ marginTop:14, background:"transparent", border:`1px solid ${T.border}`, borderRadius:7, color:T.muted, fontSize:12, fontWeight:600, padding:"7px 16px", cursor:"pointer", WebkitTapHighlightColor:"transparent" }}>
                    🔍 Lihat Detail Transfer
                  </button>
                </div>
              );
            }

            // Tampilkan transfer reminder normal (bulan ini ≤ tgl 10, atau bulan depan)
            return (
              <>
                {showDetail && (
                  <div style={{ background:T.gold+"12", border:`1px solid ${T.gold}30`, borderRadius:8, padding:"8px 12px", marginBottom:10, fontSize:11, color:T.gold, display:"flex", alignItems:"center", justifyContent:"space-between", gap:6 }}>
                    <span>📝 Menampilkan detail — ada perubahan anggaran atau uang masuk baru?</span>
                    <button onClick={() => setShowDetail(false)} style={{ background:"none", border:"none", color:T.muted, fontSize:11, cursor:"pointer", fontWeight:600, padding:0, flexShrink:0 }}>✕ Tutup</button>
                  </div>
                )}
                {!isFuture && isThisMonth && !showDetail && (
                  <div style={{ background:T.gold+"12", border:`1px solid ${T.gold}30`, borderRadius:8, padding:"8px 12px", marginBottom:10, fontSize:11, color:T.gold, display:"flex", alignItems:"center", gap:6 }}>
                    📅 Transfer sebelum tanggal 10 — sisa {10 - now.getDate()} hari lagi
                  </div>
                )}
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {byWallet.map(g => {
                    // Sebelum tgl 10: gunakan 'budget' (hanya income/transfer masuk)
                    // bukan 'balance' (income - pengeluaran) — supaya pengeluaran sebelum tgl 10
                    // tidak mempengaruhi perhitungan transfer yang masih direncanakan
                    const { budget: saldoMasuk, balance: saldoAktual } = getWalletMonth(g.wallet, transactions, month, year);
                    const saldoIni     = showDetail ? saldoAktual : saldoMasuk;
                    const perluTransfer = Math.max(0, g.totalBudget - saldoIni);
                    const sudahCukup   = saldoIni >= g.totalBudget;
                    const pctCovered   = g.totalBudget > 0 ? Math.min(saldoIni / g.totalBudget, 1) : 0;
                    const statusColor  = sudahCukup ? T.green : pctCovered > 0.5 ? T.orange : T.red;
                    const borderColor  = sudahCukup ? T.green+"33" : pctCovered > 0.5 ? T.orange+"33" : T.red+"33";

                    return (
                      <div key={g.wallet.id} style={{ background:T.surface, border:`1px solid ${borderColor}`, borderRadius:10, padding:"12px 14px" }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <div style={{ width:34, height:34, borderRadius:8, background:g.wallet.color+"25", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>{g.wallet.icon}</div>
                            <div>
                              <div style={{ color:T.text, fontWeight:700, fontSize:13 }}>{g.wallet.name}</div>
                              <div style={{ color:T.muted, fontSize:10, marginTop:1 }}>{g.cats.length} kategori</div>
                            </div>
                          </div>
                          {sudahCukup ? (
                            <span style={{ background:T.green+"18", color:T.green, borderRadius:6, padding:"4px 10px", fontSize:11, fontWeight:700 }}>✓ Sudah cukup</span>
                          ) : (
                            <div style={{ textAlign:"right" }}>
                              <div style={{ color:T.muted, fontSize:10, marginBottom:2 }}>Perlu ditransfer</div>
                              <div style={{ color:statusColor, fontFamily:"monospace", fontVariantNumeric:"tabular-nums", fontWeight:800, fontSize:16 }}>{fmtIDR(perluTransfer)}</div>
                            </div>
                          )}
                        </div>

                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6, marginBottom:8 }}>
                          {[
                            ["Anggaran",       g.totalBudget, T.text],
                            ["Saldo saat ini", saldoIni,      saldoIni > 0 ? T.green : T.muted],
                            [sudahCukup ? "Transfer" : "Kurang", perluTransfer, statusColor],
                          ].map(([label, val, col]) => (
                            <div key={label} style={{ background:T.card, borderRadius:7, padding:"7px 9px" }}>
                              <div style={{ color:T.muted, fontSize:9, fontWeight:700, letterSpacing:"0.05em", textTransform:"uppercase", marginBottom:3 }}>{label}</div>
                              <div style={{ color:col, fontFamily:"monospace", fontVariantNumeric:"tabular-nums", fontWeight:700, fontSize:12 }}>{val===0&&label!=="Kurang"?"—":(isMobile?fmtShort(val):fmtIDR(val))}</div>
                            </div>
                          ))}
                        </div>

                        {g.totalBudget > 0 && (
                          <>
                            <div style={{ height:4, background:T.border, borderRadius:3, overflow:"hidden", marginBottom:5 }}>
                              <div style={{ height:"100%", width:"100%", borderRadius:3, transformOrigin:"left", transform:`scaleX(${Math.min(pctCovered,1)})`, background:statusColor, transition:"transform 0.3s" }} />
                            </div>
                            {!sudahCukup && (
                              <div style={{ fontSize:10, color:statusColor, marginBottom:6 }}>
                                Saldo menutup {(pctCovered*100).toFixed(0)}% dari anggaran
                              </div>
                            )}
                          </>
                        )}

                        <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginTop:4 }}>
                          {g.cats.filter(c=>c.budget>0).map(cat => (
                            <span key={cat.id} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:5, padding:"2px 8px", fontSize:10, color:T.muted }}>
                              {cat.icon} {cat.name}: <span style={{ color:T.text, fontFamily:"monospace", fontVariantNumeric:"tabular-nums" }}>{fmtShort(cat.budget)}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Total summary bar */}
                {(() => {
                  const walletData = getTransferNeeded(byWallet, [], transactions, month, year);
                  const totalPerluTransfer = walletData.reduce((a, g) => {
                    const { budget: saldoM, balance: saldoA } = getWalletMonth(g.wallet, transactions, month, year);
                    const saldo = showDetail ? saldoA : saldoM;
                    return a + Math.max(0, g.totalBudget - saldo);
                  }, 0);
                  return totalPerluTransfer > 0 ? (
                    <div style={{ marginTop:12, background:T.card, border:`1px solid ${T.border}`, borderRadius:9, padding:"11px 14px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <div style={{ color:T.muted, fontSize:11 }}>Total yang perlu ditransfer</div>
                      <div style={{ color:T.gold, fontFamily:"monospace", fontVariantNumeric:"tabular-nums", fontWeight:800, fontSize:16 }}>{fmtIDR(totalPerluTransfer)}</div>
                    </div>
                  ) : (
                    <div style={{ marginTop:12, background:T.green+"10", border:`1px solid ${T.green}33`, borderRadius:9, padding:"11px 14px", textAlign:"center", color:T.green, fontSize:12, fontWeight:600 }}>
                      ✓ Semua rekening sudah memiliki dana yang cukup
                    </div>
                  );
                })()}
              </>
            );
          })()}
        </Card>
      )}

      {/* Container 1: Anggaran Pengeluaran */}
      <Card style={{ padding:16, marginBottom:14 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
          <div style={{ color:T.red, fontWeight:700, fontSize:13 }}>💸 Anggaran Pengeluaran</div>
          <span style={{ fontSize:11, fontFamily:"monospace", fontVariantNumeric:"tabular-nums", color:T.muted }}>{fmtShort(expActual)} / <span style={{ color:T.red }}>{fmtShort(expBudget)}</span></span>
        </div>
        {expData.length===0
          ? <div style={{ padding:"16px 0", textAlign:"center", color:T.muted, fontSize:12 }}>Belum ada anggaran pengeluaran</div>
          : expData.map((cat,i) => <CatRow key={cat.id} cat={cat} i={i} len={expData.length} />)}
      </Card>

      {/* Container 2: Anggaran Investasi */}
      <Card style={{ padding:16 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
          <div style={{ color:T.gold, fontWeight:700, fontSize:13 }}>📈 Anggaran Investasi</div>
          <span style={{ fontSize:11, fontFamily:"monospace", fontVariantNumeric:"tabular-nums", color:T.muted }}>{fmtShort(invActual)} / <span style={{ color:T.gold }}>{fmtShort(invBudget)}</span></span>
        </div>
        {invData.length===0
          ? <div style={{ padding:"16px 0", textAlign:"center", color:T.muted, fontSize:12 }}>Belum ada anggaran investasi.<br/><span style={{ fontSize:11 }}>Atur lewat tombol "Atur Anggaran" → tab Investasi</span></div>
          : invData.map((cat,i) => <CatRow key={cat.id} cat={cat} i={i} len={invData.length} />)}
      </Card>
    </div>
  );
}
